import { z } from "zod";

export const authModeEnum = z.enum(["none", "api-key", "oidc", "dev"]);
export type AuthMode = z.infer<typeof authModeEnum>;

const baseSchema = z.object({
  DATABASE_URL: z.string().url(),
  REDIS_URL: z.string().url(),
  AUTH_MODE: authModeEnum.default("none"),
  OPENAI_API_KEY: z.string().min(1),
  ANTHROPIC_API_KEY: z.string().min(1).optional(),
  BRAVE_SEARCH_API_KEY: z.string().min(1).optional(),
  IAMU_TENANT_ID: z.string().uuid().optional(),
  IAMU_API_KEY: z.string().min(1).optional(),
  OIDC_ISSUER_URL: z.string().url().optional(),
  OIDC_CLIENT_ID: z.string().min(1).optional(),
  WORKOS_API_KEY: z.string().min(1).optional(),
  WORKOS_CLIENT_ID: z.string().min(1).optional(),
  SESSION_TTL_SECONDS: z.coerce.number().default(86400),
  DASHBOARD_URL: z.string().url().default("http://localhost:3001"),
  DOCLING_URL: z.string().url().default("http://127.0.0.1:5001"),
  CRAWL4AI_URL: z.string().url().default("http://localhost:11235"),
  CONTEXTUAL_CHUNKING_ENABLED: z.string().default("true"),
  API_PORT: z.coerce.number().default(3000),
  NODE_ENV: z
    .enum(["development", "production", "test"])
    .default("development"),
});

export const envSchema = baseSchema.superRefine((data, ctx) => {
  if (data.AUTH_MODE === "api-key" && !data.IAMU_API_KEY) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "IAMU_API_KEY is required when AUTH_MODE=api-key",
      path: ["IAMU_API_KEY"],
    });
  }
  if (data.AUTH_MODE === "oidc") {
    const clientId = data.WORKOS_CLIENT_ID || data.OIDC_CLIENT_ID;
    const hasWorkOS = Boolean(data.WORKOS_API_KEY && clientId);
    if (!hasWorkOS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "WORKOS_API_KEY and WORKOS_CLIENT_ID are required when AUTH_MODE=oidc",
        path: ["WORKOS_API_KEY"],
      });
    }
  }
});

export type Env = z.infer<typeof baseSchema>;

export function loadEnv(): Env {
  return envSchema.parse(process.env) as Env;
}

/**
 * Check if an OpenAI API key is actually set (not missing or placeholder).
 */
export function isOpenAIKeySet(
  key: string | undefined = process.env.OPENAI_API_KEY,
): key is string {
  return !!key && key !== "sk-placeholder";
}
